import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Code2, Users, Target, Heart, Lightbulb, TrendingUp, Shield, Globe, Award, BookOpen, Zap, ArrowRight, Github, Linkedin } from 'lucide-react';
import Button from '../../components/ui/Button';

const About = () => {
  const navigate = useNavigate();
  const [selectedMilestone, setSelectedMilestone] = useState(0);

  const teamMembers = [
    {
      name: "Alex Chen",
      role: "Founder & Lead Instructor",
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=300&h=300&fit=crop&crop=face",
      bio: "Former Senior QA Engineer at Google with 8+ years in automation testing. Passionate about making complex testing concepts accessible to everyone.",
      expertise: ["Selenium", "Test Architecture", "CI/CD", "Mentoring"],
      linkedin: "https://linkedin.com/in/alexchen",
      github: "https://github.com/alexchen"
    },
    {
      name: "Sarah Rodriguez",
      role: "Platform Architect",
      avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=300&h=300&fit=crop&crop=face",
      bio: "Full-stack developer with expertise in cloud infrastructure and educational technology. Previously at Microsoft Azure team.",
      expertise: ["Cloud Architecture", "Docker", "React", "Python"],
      linkedin: "https://linkedin.com/in/sarahrodriguez",
      github: "https://github.com/sarahrodriguez"
    },
    {
      name: "Michael Patel",
      role: "Curriculum Designer",
      avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&h=300&fit=crop&crop=face",
      bio: "Educational specialist with a background in instructional design and gamified learning experiences. Former Udemy course creator.",
      expertise: ["Learning Design", "Gamification", "Content Strategy", "User Experience"],
      linkedin: "https://linkedin.com/in/michaelpatel",
      github: "https://github.com/michaelpatel"
    }
  ];

  const milestones = [
    {
      date: "January 2023",
      title: "Project Launch",
      description: "Initial concept and market research for hands-on automation testing education.",
      icon: <Lightbulb className="w-6 h-6" />,
      stats: { users: 0, missions: 0, features: 1 }
    },
    {
      date: "March 2023",
      title: "MVP Development",
      description: "Built the core platform with basic code editor and browser execution environment.",
      icon: <Code2 className="w-6 h-6" />,
      stats: { users: 50, missions: 10, features: 5 }
    },
    {
      date: "June 2023",
      title: "Beta Launch",
      description: "Released to a select group of beta testers and gathered initial feedback.",
      icon: <Users className="w-6 h-6" />,
      stats: { users: 250, missions: 25, features: 12 }
    },
    {
      date: "September 2023",
      title: "Gamification Update",
      description: "Added mission-based progression system with achievements and XP rewards.",
      icon: <Award className="w-6 h-6" />,
      stats: { users: 800, missions: 50, features: 20 }
    },
    {
      date: "December 2023",
      title: "Interview Prep Module",
      description: "Launched comprehensive interview preparation with real-world scenarios.",
      icon: <Target className="w-6 h-6" />,
      stats: { users: 1500, missions: 85, features: 30 }
    },
    {
      date: "March 2024",
      title: "Major Platform Upgrade",
      description: "Enhanced cloud infrastructure for better performance and scalability.",
      icon: <Zap className="w-6 h-6" />,
      stats: { users: 2500, missions: 150, features: 45 }
    }
  ];

  const values = [
    {
      icon: <Heart className="w-8 h-8" />,
      title: "Accessibility First",
      description: "We believe quality education should be accessible to everyone, regardless of background or financial situation."
    },
    {
      icon: <BookOpen className="w-8 h-8" />,
      title: "Learning by Doing",
      description: "Hands-on practice with real tools and environments provides the most effective learning experience."
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Community Driven",
      description: "Our platform grows stronger through community feedback, contributions, and shared knowledge."
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Quality Focused",
      description: "Every mission and feature is carefully crafted to provide maximum educational value and practical relevance."
    }
  ];

  const techStack = [
    { name: "Docker", description: "Secure, isolated execution environments" },
    { name: "React", description: "Modern, responsive user interface" },
    { name: "Python", description: "Powerful backend automation engine" },
    { name: "Selenium", description: "Industry-standard web automation" },
    { name: "AWS", description: "Scalable cloud infrastructure" },
    { name: "MongoDB", description: "Flexible data storage solution" }
  ];

  const achievements = [
    { value: "2,500+", label: "Active Learners", icon: <Users className="w-6 h-6" /> },
    { value: "150+", label: "Practice Missions", icon: <Target className="w-6 h-6" /> },
    { value: "94%", label: "Success Rate", icon: <TrendingUp className="w-6 h-6" /> },
    { value: "500+", label: "Career Transitions", icon: <Award className="w-6 h-6" /> }
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/90 backdrop-blur-md border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/homepage" className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                <Code2 className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold">Selenium Sandbox</span>
            </Link>
            
            <div className="hidden md:flex items-center space-x-8">
              <Link to="/homepage" className="text-muted-foreground hover:text-foreground transition-colors">Home</Link>
              <Link to="/roadmap" className="text-muted-foreground hover:text-foreground transition-colors">Roadmap</Link>
              <Link to="/tutorials" className="text-muted-foreground hover:text-foreground transition-colors">Tutorials</Link>
              <Link to="/challenges" className="text-muted-foreground hover:text-foreground transition-colors">Challenges</Link>
              <Link to="/interview-prep" className="text-muted-foreground hover:text-foreground transition-colors">Interview Prep</Link>
              <Link to="/contact" className="text-muted-foreground hover:text-foreground transition-colors">Contact</Link>
            </div>

            <div className="flex items-center space-x-3">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate('/user-login')}
              >
                Login
              </Button>
              <Button 
                size="sm"
                onClick={() => navigate('/user-registration')}
              >
                Start Learning
              </Button>
            </div>
          </div>
        </div>
      </nav>
      {/* Hero Section */}
      <section className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl lg:text-5xl font-bold mb-6">
              About 
              <span className="bg-gradient-to-r from-blue-500 to-purple-600 bg-clip-text text-transparent">
                {" "}Selenium Sandbox
              </span>
            </h1>
            <p className="text-xl text-muted-foreground leading-relaxed">
              Empowering developers worldwide to master web automation testing through 
              practical, hands-on learning experiences that bridge the gap between 
              education and real-world application.
            </p>
          </motion.div>
        </div>
      </section>
      {/* Mission Statement */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-muted/5">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold mb-6">Our Mission</h2>
              <div className="space-y-4 text-lg text-muted-foreground">
                <p>
                  We're on a mission to democratize automation testing education by providing 
                  a comprehensive, hands-on learning platform that bridges the gap between 
                  theoretical knowledge and practical industry skills.
                </p>
                <p>
                  Our platform empowers developers at all skill levels to master Selenium 
                  automation through real-world missions, gamified progression, and 
                  immediate feedback in a safe, cloud-based environment.
                </p>
              </div>
              
              <div className="mt-8">
                <h3 className="text-xl font-semibold mb-4">Our Vision</h3>
                <p className="text-muted-foreground">
                  To become the world's most trusted platform for practical automation 
                  testing education, enabling millions of developers to advance their 
                  careers and build better, more reliable web applications.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-6"
            >
              {achievements?.map((achievement, index) => (
                <div key={index} className="bg-card border border-border rounded-xl p-6 text-center">
                  <div className="flex justify-center mb-3 text-blue-500">
                    {achievement?.icon}
                  </div>
                  <div className="text-3xl font-bold mb-2">{achievement?.value}</div>
                  <div className="text-sm text-muted-foreground">{achievement?.label}</div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>
      {/* Who This Is For */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Who This Platform Is For</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Whether you're just starting your testing journey or looking to advance 
              your automation skills, our platform adapts to your learning needs.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                title: "Complete Beginners",
                description: "No prior testing experience? Start with our foundation missions that teach you the basics of web automation.",
                icon: <BookOpen className="w-8 h-8" />
              },
              {
                title: "Manual Testers",
                description: "Ready to transition to automation? Our guided path helps you apply your testing knowledge to Selenium.",
                icon: <ArrowRight className="w-8 h-8" />
              },
              {
                title: "Developers",
                description: "Want to add testing skills to your toolkit? Learn how to build robust automation frameworks.",
                icon: <Code2 className="w-8 h-8" />
              },
              {
                title: "Job Seekers",
                description: "Preparing for automation testing interviews? Practice with real scenarios and common questions.",
                icon: <Target className="w-8 h-8" />
              }
            ]?.map((audience, index) => (
              <motion.div
                key={index}
                className="bg-card border border-border rounded-xl p-6 text-center hover:shadow-elevation-2 transition-all duration-300"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                  {React.cloneElement(audience?.icon, { className: "w-8 h-8 text-white" })}
                </div>
                <h3 className="text-xl font-semibold mb-3">{audience?.title}</h3>
                <p className="text-muted-foreground">{audience?.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      {/* Team Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-muted/5">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Meet Our Team</h2>
            <p className="text-lg text-muted-foreground">
              Passionate educators and technologists dedicated to transforming how 
              automation testing is learned and practiced.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {teamMembers?.map((member, index) => (
              <motion.div
                key={index}
                className="bg-card border border-border rounded-xl p-6 text-center"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <img
                  src={member?.avatar}
                  alt={member?.name}
                  className="w-24 h-24 rounded-full mx-auto mb-4 object-cover"
                />
                <h3 className="text-xl font-bold mb-2">{member?.name}</h3>
                <p className="text-blue-500 font-semibold mb-4">{member?.role}</p>
                <p className="text-muted-foreground text-sm mb-4">{member?.bio}</p>
                
                <div className="flex flex-wrap justify-center gap-2 mb-4">
                  {member?.expertise?.map((skill, skillIndex) => (
                    <span
                      key={skillIndex}
                      className="px-2 py-1 bg-muted text-xs rounded-full"
                    >
                      {skill}
                    </span>
                  ))}
                </div>

                <div className="flex justify-center space-x-3">
                  <a
                    href={member?.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-500 hover:text-blue-400 transition-colors"
                  >
                    <Linkedin className="w-5 h-5" />
                  </a>
                  <a
                    href={member?.github}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Github className="w-5 h-5" />
                  </a>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      {/* Timeline */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Our Journey</h2>
            <p className="text-lg text-muted-foreground">
              From concept to comprehensive platform - explore the key milestones 
              that shaped Selenium Sandbox.
            </p>
          </div>

          <div className="relative">
            {/* Desktop Timeline */}
            <div className="hidden lg:block">
              <div className="flex justify-between mb-8">
                {milestones?.map((milestone, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedMilestone(index)}
                    className={`flex flex-col items-center p-4 rounded-lg transition-all ${
                      selectedMilestone === index
                        ? 'bg-blue-500/10 border-2 border-blue-500' :'bg-card border border-border hover:border-blue-500/50'
                    }`}
                  >
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 ${
                      selectedMilestone === index
                        ? 'bg-blue-500 text-white' :'bg-muted'
                    }`}>
                      {milestone?.icon}
                    </div>
                    <div className="text-sm text-muted-foreground mb-1">{milestone?.date}</div>
                    <div className="font-semibold text-center">{milestone?.title}</div>
                  </button>
                ))}
              </div>

              <motion.div
                key={selectedMilestone}
                className="bg-card border border-border rounded-xl p-8"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="flex items-start space-x-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    {React.cloneElement(milestones?.[selectedMilestone]?.icon, { className: "w-8 h-8 text-white" })}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold mb-2">{milestones?.[selectedMilestone]?.title}</h3>
                    <p className="text-muted-foreground mb-6">{milestones?.[selectedMilestone]?.description}</p>
                    
                    <div className="grid grid-cols-3 gap-6">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-blue-500">{milestones?.[selectedMilestone]?.stats?.users?.toLocaleString()}</div>
                        <div className="text-sm text-muted-foreground">Users</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-purple-500">{milestones?.[selectedMilestone]?.stats?.missions}</div>
                        <div className="text-sm text-muted-foreground">Missions</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-green-500">{milestones?.[selectedMilestone]?.stats?.features}</div>
                        <div className="text-sm text-muted-foreground">Features</div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Mobile Timeline */}
            <div className="lg:hidden space-y-8">
              {milestones?.map((milestone, index) => (
                <motion.div
                  key={index}
                  className="flex space-x-6"
                  initial={{ opacity: 0, x: -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className="flex flex-col items-center">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                      {React.cloneElement(milestone?.icon, { className: "w-6 h-6 text-white" })}
                    </div>
                    {index < milestones?.length - 1 && (
                      <div className="w-px h-16 bg-border mt-4"></div>
                    )}
                  </div>
                  <div className="flex-1 pb-8">
                    <div className="text-sm text-muted-foreground mb-1">{milestone?.date}</div>
                    <h3 className="text-xl font-bold mb-2">{milestone?.title}</h3>
                    <p className="text-muted-foreground">{milestone?.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>
      {/* Values */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-muted/5">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Our Values</h2>
            <p className="text-lg text-muted-foreground">
              The principles that guide our mission to transform automation testing education.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {values?.map((value, index) => (
              <motion.div
                key={index}
                className="bg-card border border-border rounded-xl p-6"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="flex items-start space-x-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    {React.cloneElement(value?.icon, { className: "w-8 h-8 text-white" })}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-3">{value?.title}</h3>
                    <p className="text-muted-foreground">{value?.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      {/* Technology Stack */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Our Technology Stack</h2>
            <p className="text-lg text-muted-foreground">
              Built with modern, reliable technologies to ensure the best learning experience.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {techStack?.map((tech, index) => (
              <motion.div
                key={index}
                className="bg-card border border-border rounded-xl p-6 text-center hover:shadow-elevation-2 transition-all duration-300"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <h3 className="text-lg font-bold mb-2">{tech?.name}</h3>
                <p className="text-muted-foreground text-sm">{tech?.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-500/10 to-purple-600/10">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Start Your Journey?</h2>
          <p className="text-lg text-muted-foreground mb-8">
            Join thousands of developers who are mastering automation testing with our hands-on platform.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              onClick={() => navigate('/user-registration')}
              className="px-8 py-4"
            >
              Start Learning Free
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button 
              variant="outline" 
              size="lg" 
              onClick={() => navigate('/roadmap')}
              className="px-8 py-4"
            >
              Explore Roadmap
            </Button>
          </div>
        </div>
      </section>
      {/* Footer */}
      <footer className="bg-card border-t border-border py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                  <Code2 className="w-6 h-6 text-white" />
                </div>
                <span className="text-xl font-bold">Selenium Sandbox</span>
              </div>
              <p className="text-muted-foreground">
                Master web automation testing through hands-on practice and real-world missions.
              </p>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Learning</h3>
              <div className="space-y-3">
                <Link to="/roadmap" className="block text-muted-foreground hover:text-foreground transition-colors">
                  Roadmap
                </Link>
                <Link to="/tutorials" className="block text-muted-foreground hover:text-foreground transition-colors">
                  Tutorials
                </Link>
                <Link to="/challenges" className="block text-muted-foreground hover:text-foreground transition-colors">
                  Challenges
                </Link>
                <Link to="/interview-prep" className="block text-muted-foreground hover:text-foreground transition-colors">
                  Interview Prep
                </Link>
              </div>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Company</h3>
              <div className="space-y-3">
                <Link to="/homepage" className="block text-muted-foreground hover:text-foreground transition-colors">
                  Home
                </Link>
                <Link to="/contact" className="block text-muted-foreground hover:text-foreground transition-colors">
                  Contact
                </Link>
                <a href="#" className="block text-muted-foreground hover:text-foreground transition-colors">
                  Privacy Policy
                </a>
                <a href="#" className="block text-muted-foreground hover:text-foreground transition-colors">
                  Terms of Service
                </a>
              </div>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Community</h3>
              <div className="space-y-3">
                <a href="https://github.com" className="flex items-center space-x-2 text-muted-foreground hover:text-foreground transition-colors">
                  <Github className="w-4 h-4" />
                  <span>GitHub</span>
                </a>
                <a href="https://linkedin.com" className="flex items-center space-x-2 text-muted-foreground hover:text-foreground transition-colors">
                  <Linkedin className="w-4 h-4" />
                  <span>LinkedIn</span>
                </a>
                <a href="#" className="flex items-center space-x-2 text-muted-foreground hover:text-foreground transition-colors">
                  <Globe className="w-4 h-4" />
                  <span>Blog</span>
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-border mt-12 pt-8 text-center text-muted-foreground">
            <p>&copy; 2025 Selenium Sandbox. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default About;